import { useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { NarrationSceneClassicList } from '@/components/NarrationSceneClassicList';
import { CinematicFilmReelSceneList } from '@/components/narration/CinematicFilmReelSceneList';
import { ImageStoryPanelRetryButton } from '@/components/narration/ImageStoryPanelRetryButton';
import { NarrationSceneAudioAttach } from '@/components/narration/NarrationSceneAudioAttach';
import { SceneListViewToggle } from '@/components/narration/SceneListViewToggle';
import { sceneImageUrlWithCropRevision } from '@/lib/imageStoryCrop';
import { buildImageStoryReelItemFromScene } from '@/lib/imageStoryPanelImageState';
import {
  narrationTextForScene,
  sceneSkipsNarration,
} from '@/lib/narrationSceneScripts';
import {
  isReelViewMode,
  reelOrientationForViewMode,
  SCENE_LIST_VIEW,
  useSceneListViewMode,
  writeSceneListViewMode,
} from '@/lib/sceneListViewMode';

export function createNarrationSceneClientId() {
  return `scene-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function sceneRowKey(scene, index) {
  return scene?.client_id || `row-${scene?.image_url || index}-${scene?.segment_number || index}`;
}

/**
 * Scene list with three views: classic list, horizontal reel, vertical reel.
 */
export function NarrationSceneReorderList({
  scenes = [],
  onReorder,
  onRemove,
  onSelectScene,
  onToggleSkipNarration,
  onSilentDurationChange,
  onRetryImage,
  onSceneAudioChange,
  imageRetryBusy = null,
  disabled = false,
  className = '',
  sidebar = false,
  viewMode: controlledViewMode,
  onViewModeChange,
  showViewToggle = true,
  storageKey = 'narration-scene-list-view',
}) {
  const [internalViewMode, setInternalViewMode] = useSceneListViewMode(storageKey, {
    defaultView: SCENE_LIST_VIEW.LIST,
  });
  const viewMode = controlledViewMode ?? internalViewMode;

  const setViewMode = (next) => {
    if (controlledViewMode == null) setInternalViewMode(next);
    else writeSceneListViewMode(next, storageKey);
    onViewModeChange?.(next);
  };

  const reelItems = useMemo(
    () => scenes.map((scene, index) => {
      const narration = sceneSkipsNarration(scene)
        ? 'Visual only — no narrator voice'
        : (narrationTextForScene(scene) || 'Add narration via Update script');
      const item = buildImageStoryReelItemFromScene(scene, index, {
        resolveImageUrl: (row) => (
          row.image_url ? sceneImageUrlWithCropRevision(row.image_url, row) : null
        ),
      });
      return {
        ...item,
        id: sceneRowKey(scene, index),
        sections: [
          {
            key: 'narration',
            label: 'Narration script',
            content: narration,
            defaultOpen: true,
          },
          ...(scene.scene_text || scene.text ? [{
            key: 'scene',
            label: 'Scene description',
            content: String(scene.scene_text || scene.text).trim(),
          }] : []),
          ...(scene.audio_url ? [{
            key: 'audio',
            label: 'Attached audio',
            content: `${scene.attached_audio_filename || 'Audio clip'}${
              Number(scene.audio_duration || scene.duration) > 0
                ? ` (${Number(scene.audio_duration || scene.duration).toFixed(1)}s)`
                : ''
            }`,
          }] : []),
        ],
      };
    }),
    [scenes],
  );

  const handleMove = (index, delta) => {
    if (disabled) return;
    const to = index + delta;
    if (to < 0 || to >= scenes.length) return;
    onReorder(index, to);
  };

  if (!scenes.length) return null;

  const reelExtra = (item) => {
    const scene = item.scene;
    const panelImage = item.panelImage;
    const retryBusy = imageRetryBusy === panelImage?.panelNumber;
    return (
      <div className="space-y-2 text-[11px]">
        <ImageStoryPanelRetryButton
          panelImage={panelImage}
          busy={retryBusy}
          variant="reelExpanded"
          onClick={() => onRetryImage?.(scene)}
        />
        {panelImage?.error ? (
          <p className="text-[10px] text-red-300 leading-relaxed">{panelImage.error}</p>
        ) : null}
        <div className="flex items-center gap-2">
          <Switch
            id={`skip-narration-${scene.client_id || scene.segment_number}`}
            checked={sceneSkipsNarration(scene)}
            disabled={disabled}
            onCheckedChange={(checked) =>
              onToggleSkipNarration?.(
                scene.segment_number,
                checked,
                scene.client_id,
              )
            }
            data-testid={`skip-narration-scene-${scene.segment_number}`}
          />
          <Label
            htmlFor={`skip-narration-${scene.client_id || scene.segment_number}`}
            className="text-[11px] text-slate-200 cursor-pointer"
          >
            No narration
          </Label>
        </div>
        {sceneSkipsNarration(scene) ? (
          <div className="flex items-center gap-1.5">
            <Label
              htmlFor={`silent-duration-${scene.client_id || scene.segment_number}`}
              className="text-[10px] text-slate-300 whitespace-nowrap"
            >
              Hold (sec)
            </Label>
            <Input
              id={`silent-duration-${scene.client_id || scene.segment_number}`}
              type="number"
              min={1}
              max={60}
              step={0.5}
              value={Number(scene.duration) || 3}
              disabled={disabled}
              onChange={(e) =>
                onSilentDurationChange?.(
                  scene.segment_number,
                  e.target.value,
                  scene.client_id,
                )
              }
              className="h-7 w-16 px-2 text-xs rounded-lg bg-black/30 border-white/15 text-white"
              data-testid={`silent-duration-scene-${scene.segment_number}`}
            />
          </div>
        ) : null}
        <NarrationSceneAudioAttach
          scene={scene}
          disabled={disabled}
          dark
          onAudioChange={onSceneAudioChange}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 text-[10px] text-red-300 hover:text-red-200 hover:bg-red-500/10 w-full justify-start px-2"
          disabled={disabled}
          onClick={() => onRemove?.(scene.segment_number)}
          aria-label={`Remove scene ${scene.segment_number}`}
          data-testid={`remove-image-story-scene-${scene.segment_number}`}
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Remove scene
        </Button>
      </div>
    );
  };

  return (
    <div className={className} data-testid="narration-scene-reorder-list">
      {showViewToggle ? (
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-400/90">
            {scenes.length} scene{scenes.length === 1 ? '' : 's'}
          </p>
          <SceneListViewToggle
            value={viewMode}
            onChange={setViewMode}
            disabled={disabled}
            compact
            dark={sidebar && isReelViewMode(viewMode)}
          />
        </div>
      ) : null}

      {viewMode === SCENE_LIST_VIEW.LIST ? (
        <NarrationSceneClassicList
          scenes={scenes}
          onReorder={onReorder}
          onRemove={onRemove}
          onSelectScene={onSelectScene}
          onToggleSkipNarration={onToggleSkipNarration}
          onSilentDurationChange={onSilentDurationChange}
          onRetryImage={onRetryImage}
          onSceneAudioChange={onSceneAudioChange}
          imageRetryBusy={imageRetryBusy}
          disabled={disabled}
        />
      ) : (
        <CinematicFilmReelSceneList
          items={reelItems}
          disabled={disabled}
          orientation={reelOrientationForViewMode(viewMode)}
          resizable
          panelMaxHeight={560}
          fullWidth={viewMode === SCENE_LIST_VIEW.REEL_HORIZONTAL}
          className={[
            sidebar ? 'cinematic-film-reel--sidebar' : '',
            'cinematic-film-reel--wide',
          ].filter(Boolean).join(' ')}
          header={(
            <p className="cinematic-film-reel__toolbar-title">
              {viewMode === SCENE_LIST_VIEW.REEL_HORIZONTAL ? 'Horizontal reel' : 'Vertical reel'}
            </p>
          )}
          onSelectItem={(item) => onSelectScene?.(item.scene)}
          onMoveItem={handleMove}
          onRetryItem={(item) => onRetryImage?.(item.scene)}
          imageRetryBusy={imageRetryBusy}
          renderExtra={reelExtra}
        />
      )}
    </div>
  );
}

export { isReelViewMode, SCENE_LIST_VIEW, useSceneListViewMode };
